import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, FolderInput, Loader2, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const NO_FILTERS = { q: "", category: "", min: "", max: "", image: "", status: "" };

export const applyFilters = (list, f) => {
  const q = f.q.toLowerCase();
  const min = f.min === "" ? null : Number(f.min);
  const max = f.max === "" ? null : Number(f.max);
  return list.filter((p) => {
    if (q && !`${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(q)) return false;
    if (f.category === "__none" ? p.category : f.category && p.category !== f.category) return false;
    if (min !== null && !(p.price >= min)) return false;
    if (max !== null && !(p.price <= max)) return false;
    if (f.image === "with" && !p.images?.length) return false;
    if (f.image === "without" && p.images?.length) return false;
    if (f.status === "shown" && !p.active) return false;
    if (f.status === "hidden" && p.active) return false;
    return true;
  });
};

export const ShopFilters = ({ data, f, setF }) => {
  const cats = [...new Set(data.map((p) => p.category).filter(Boolean))].sort();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const active = Object.entries(f).some(([k, v]) => v !== NO_FILTERS[k]);
  return (
    <div className="shop-admin-filters" data-testid="product-filters">
      <input className="shop-search" placeholder={`Search ${data.length} products`} value={f.q} onChange={set("q")} data-testid="product-search-input" />
      <select value={f.category} onChange={set("category")} aria-label="Category" data-testid="product-filter-category">
        <option value="">All categories</option>
        <option value="__none">No category ({data.filter((p) => !p.category).length})</option>
        {cats.map((c) => <option key={c} value={c}>{c} ({data.filter((p) => p.category === c).length})</option>)}
      </select>
      <div className="shop-price">
        <input type="number" min="0" inputMode="decimal" placeholder="Min $" value={f.min} onChange={set("min")} aria-label="Minimum price" data-testid="product-filter-min" />
        <span>–</span>
        <input type="number" min="0" inputMode="decimal" placeholder="Max $" value={f.max} onChange={set("max")} aria-label="Maximum price" data-testid="product-filter-max" />
      </div>
      <select value={f.image} onChange={set("image")} aria-label="Photo" data-testid="product-filter-image">
        <option value="">With or without photo</option>
        <option value="with">Has a photo</option>
        <option value="without">No photo</option>
      </select>
      <select value={f.status} onChange={set("status")} aria-label="Shown or hidden" data-testid="product-filter-status">
        <option value="">Shown & hidden</option>
        <option value="shown">Shown on site ({data.filter((p) => p.active).length})</option>
        <option value="hidden">Hidden ({data.filter((p) => !p.active).length})</option>
      </select>
      {active && <button type="button" className="btn light" onClick={() => setF(NO_FILTERS)} data-testid="product-filter-clear"><X size={14} /> Clear filters</button>}
    </div>
  );
};

const TASKS = {
  categories: { field: "category", label: "add missing categories", noun: "a category", done: "categories", testId: "product-ai-categorize-button" },
  descriptions: { field: "description", label: "write missing descriptions", noun: "a description", done: "descriptions", testId: "product-ai-descriptions-button" },
  directions: { field: "instructions", label: "write missing directions", noun: "directions for use", done: "directions", testId: "product-ai-directions-button" },
};

export const AiButton = ({ task, data, onDone }) => {
  const t = TASKS[task];
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const missing = data.filter((p) => !p[t.field]).length;
  const url = `/admin/products/ai/${task}`;

  const poll = async () => {
    try {
      const { data: s } = await api.get(url);
      setJob(s);
      if (s.running) { timer.current = setTimeout(poll, 2000); return; }
      if (s.error) toast.error(s.error);
      else toast.success(`AI added ${t.done} to ${s.updated} product${s.updated === 1 ? "" : "s"}${s.missing ? ` — ${s.missing} skipped or still missing` : ""}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); setJob(null); }
  };
  useEffect(() => {
    api.get(url).then(({ data: s }) => { if (s.running) { setJob(s); timer.current = setTimeout(poll, 2000); } }).catch(() => {});
    return () => clearTimeout(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (!window.confirm(`Let AI write ${t.noun} for the ${missing} product${missing === 1 ? "" : "s"} without ${t.noun}? Products that already have ${t.noun} won't change.`)) return;
    try { const { data: s } = await api.post(url); setJob(s); timer.current = setTimeout(poll, 2000); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const running = job?.running;
  return (
    <button type="button" className="btn shop-ai-btn" onClick={start} disabled={running || !missing} data-testid={t.testId}
      title={missing ? "" : `Every product already has ${t.noun}`}>
      {running ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
      {running ? `AI working… ${job.done}/${job.total}` : `AI: ${t.label}${missing ? ` (${missing})` : ""}`}
    </button>
  );
};

export const MoveCategory = ({ data, sel, onMoved }) => {
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const cats = [...new Set(data.map((p) => p.category).filter(Boolean))].sort();
  const move = async () => {
    const name = cat.trim();
    if (!name) { toast.error("Choose or type a category first"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/products/bulk-category", { ids: [...sel], category: name });
      toast.success(`${r.updated} product${r.updated === 1 ? "" : "s"} moved to ${name}`);
      setCat(""); onMoved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <span className="shop-move">
      <input list="shop-cat-list" value={cat} onChange={(e) => setCat(e.target.value)} placeholder="Move to category…" maxLength={60} aria-label="Move to category" data-testid="product-bulk-category-input" />
      <datalist id="shop-cat-list">{cats.map((c) => <option key={c} value={c} />)}</datalist>
      <button type="button" className="btn light" onClick={move} disabled={busy} data-testid="product-bulk-category-button">
        {busy ? <Loader2 size={14} className="spin" /> : <FolderInput size={14} />} Move
      </button>
    </span>
  );
};

export const ShowHide = ({ sel, onDone }) => {
  const [busy, setBusy] = useState(null);
  const run = async (active) => {
    setBusy(active ? "show" : "hide");
    try {
      const { data: r } = await api.post("/admin/products/bulk-active", { ids: [...sel], active });
      toast.success(`${r.updated} product${r.updated === 1 ? "" : "s"} ${active ? "now showing in the shop" : "hidden from the shop"}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  return (
    <span className="shop-showhide">
      <button type="button" className="btn light" onClick={() => run(true)} disabled={!!busy} data-testid="product-bulk-show-button">
        {busy === "show" ? <Loader2 size={14} className="spin" /> : <Eye size={14} />} Show on site
      </button>
      <button type="button" className="btn light" onClick={() => run(false)} disabled={!!busy} data-testid="product-bulk-hide-button">
        {busy === "hide" ? <Loader2 size={14} className="spin" /> : <EyeOff size={14} />} Hide from site
      </button>
    </span>
  );
};
